import { Status, StatusType } from "@brexhq/component-library";
import styled from "@emotion/styled";
import { m } from "framer-motion";
import React from "react";

import { BlockContent } from "@brexhq/next/src/components/deprecated/BlockContent";
import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { convertSpacerValueToNumeric } from "@brexhq/next/src/components/layout/Spacers/Spacers";
import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import ArrowRightDisplay from "@brexhq/shared/assets/icons/single-color/ArrowRightDisplay";
import ArrowRightMono from "@brexhq/shared/assets/icons/single-color/ArrowRightMono";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { isGradientColor } from "@brexhq/shared/components/Typography/types";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { SanityTextRowProps } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems";
import {
  TextStyle,
  ThemeTextComponents,
} from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextRow";

import {
  processBlockContentForCustomComponents,
  type ProcessedBlockContent,
} from "./helpers";
import { useArrowSize } from "./hooks/useArrowSize";

interface Props
  extends Omit<SanityTextRowProps, "_key" | "_type" | "_rawContent"> {
  theme: ModuleThemeType;
  blockContent: ProcessedBlockContent[];
  textColorOverride?: string;
  absolutePositioning?: boolean;
  absolutePositioningAdvancedOptions?: {
    position: "top" | "bottom";
    offset: number;
  };
  index: number;
  shouldAnimate: boolean;
  noWrap?: boolean;
}

const TextWithPill = styled.div<{
  pill: SanityTextRowProps["pill"];
}>(({ theme, pill }) => ({
  display: "flex",
  gap: theme.spacing[8],
  alignItems: pill?.verticalAlignment || "center",
}));

const TextMaxWidth = styled.div<{
  maxWidth?: number | null;
  hangingPunctuation?: number;
}>(({ maxWidth, theme, hangingPunctuation }) => {
  const maxWidthValue = Number(maxWidth) ? `${maxWidth}%` : "100%";
  return {
    ...(hangingPunctuation && { textIndent: `-${hangingPunctuation}px` }),
    [theme.breakpoints.LRG]: {
      maxWidth: maxWidthValue,
    },
  };
});

const TextAnimationWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "textStyle",
})<{
  textStyle: TextStyle;
}>(({ textStyle }) => {
  const textComponent = ThemeTextComponents[textStyle];

  // Clamp font size and line height for Title styles to keep animated text on one line.
  const FONT_SIZES_CLAMP_VALUES = {
    "Title L": "clamp(3rem, 0.3333rem + 4.1667vw, 4.5rem)",
    "Title M": "clamp(1.875rem, 0.5599rem + 2.7397vw, 3rem)",
    "Title S": "clamp(1.5rem, 0.6233rem + 1.8265vw, 2.25rem)",
  };

  const LINE_HEIGHTS_CLAMP_VALUES = {
    "Title L": "clamp(2.25rem, -0.2749rem + 5.2603vw, 4.41rem)",
    "Title M": "clamp(2.25rem, 1.3733rem + 1.8265vw, 3rem)",
    "Title S": "clamp(1.5rem, 0.6233rem + 1.8265vw, 2.25rem)",
  };

  return {
    width: "100%",
    overflow: "hidden",
    paddingBottom: "clamp(0.1875rem, -0.2569rem + 0.6944vw, 0.4375rem)",
    ...(textComponent &&
    textComponent.as &&
    // Only add clamps for Title styles, since Display styles are already clamped
    textStyle in FONT_SIZES_CLAMP_VALUES
      ? {
          [textComponent.as]: {
            fontSize: `${FONT_SIZES_CLAMP_VALUES[textStyle]} !important`,
            lineHeight: `${LINE_HEIGHTS_CLAMP_VALUES[textStyle]} !important`,
          },
        }
      : {}),
  };
});

export const TextRow = ({
  index,
  theme,
  marginTop,
  minHeight,
  semanticTagOverride,
  fontWeightOverride,
  textStyle,
  textColor,
  fontFamily,
  blockContent,
  maxWidth,
  pill,
  absolutePositioning,
  absolutePositioningAdvancedOptions,
  textAnimation,
  textAnimationSlideInUp,
  shouldAnimate,
  textColorOverride,
  noWrap,
  hangingPunctuation,
}: Props) => {
  const { standard: moduleTheme } = getModuleThemeColors(theme);
  const topMarginSML = convertSpacerValueToNumeric(marginTop, "SML");
  const topMarginLRG = convertSpacerValueToNumeric(marginTop, "LRG");
  const arrowSize = useArrowSize(textStyle);

  const shouldReplaceSpaceMonoArrows = textStyle.includes("Space Mono");
  const shouldReplaceArrows = textStyle.includes("Display");
  const content = blockContent || [];

  const processedBlockContent = shouldReplaceArrows
    ? processBlockContentForCustomComponents(content, [
        {
          pattern: "->",
          componentType: shouldReplaceSpaceMonoArrows
            ? "arrowRightMono"
            : "arrowRightDisplay",
        },
      ])
    : content;

  const blockContentOptions = shouldReplaceArrows
    ? {
        types: {
          arrowRightMono: () => {
            return <ArrowRightMono width={arrowSize} height={arrowSize} />;
          },
          arrowRightDisplay: () => {
            return <ArrowRightDisplay width={arrowSize} height={arrowSize} />;
          },
        },
      }
    : {};

  const textRowContent = (
    <TextMaxWidth maxWidth={maxWidth} hangingPunctuation={hangingPunctuation}>
      <Text
        textAnimation={textAnimation}
        fontFamily={fontFamily}
        as={semanticTagOverride ?? ThemeTextComponents[textStyle]?.as}
        components={ThemeTextComponents[textStyle]?.components}
        margin={{
          LRG: [minHeight ? 0 : topMarginLRG, 0, 0, 0],
          MED: [minHeight ? 0 : topMarginLRG, 0, 0, 0],
          SML: [minHeight ? 0 : topMarginSML, 0, 0, 0],
        }}
        color={
          textColorOverride
            ? textColorOverride
            : !isGradientColor(textColor)
            ? moduleTheme[textColor]
            : undefined
        }
        gradient={
          isGradientColor(textColor) ? moduleTheme[textColor] : undefined
        }
        fontWeightOverride={fontWeightOverride || undefined}
        noWrap={noWrap}
      >
        {pill?.text ? (
          <TextWithPill pill={pill}>
            <BlockContent
              blocks={processedBlockContent}
              defaultStyleRenderer="None"
              renderChildAsSpan
              options={blockContentOptions}
              isInView={shouldAnimate}
            />
            <Status noWrap label={pill.text} status={getStatus({ pill })} />
          </TextWithPill>
        ) : (
          <BlockContent
            blocks={processedBlockContent}
            defaultStyleRenderer="None"
            renderChildAsSpan
            options={blockContentOptions}
            isInView={shouldAnimate}
          />
        )}
      </Text>
    </TextMaxWidth>
  );

  const absolutePositioningStyles = {
    position: "absolute",
    [absolutePositioningAdvancedOptions?.position as string]: `${
      absolutePositioningAdvancedOptions?.offset ?? 0
    }px`,
  };

  const containerStyles = absolutePositioning
    ? {
        ...absolutePositioningStyles,
      }
    : {};

  const textRow = minHeight ? (
    <Container
      {...containerStyles}
      minHeight={minHeight}
      // move margin spacing to the wrapper container when minHeight is set
      margin={[marginTop, 0, 0, 0]}
    >
      {textRowContent}
    </Container>
  ) : (
    <Container {...containerStyles}>{textRowContent}</Container>
  );

  if (textAnimationSlideInUp) {
    return (
      <TextAnimationSlideInUp
        index={index}
        shouldAnimate={shouldAnimate}
        textStyle={textStyle}
      >
        {textRow}
      </TextAnimationSlideInUp>
    );
  }

  return textRow;
};

function getStatus(props: { pill: SanityTextRowProps["pill"] }) {
  switch (props.pill?.style) {
    case "success":
      return StatusType.SUCCESS;
    case "danger":
      return StatusType.DANGER;
    case "warning":
      return StatusType.WARNING;
    case "neutral":
      return StatusType.NEUTRAL;
    case "incomplete":
      return StatusType.INCOMPLETE;
    case "accepted":
      return StatusType.ACCEPTED;
    default:
      return StatusType.SUCCESS;
  }
}

interface TextAnimationSlideInUpProps extends React.PropsWithChildren {
  index: number;
  shouldAnimate: boolean;
  textStyle: SanityTextRowProps["textStyle"];
}

const ANIMATION_DURATION = 0.8;
const INITIAL_Y_POSITION = 100;

const ANIMATION_DELAY_DIFFERENCE = 0.1;

const TextAnimationSlideInUp = ({
  children,
  index,
  shouldAnimate,
  textStyle,
  ...rest
}: TextAnimationSlideInUpProps) => {
  const delay =
    typeof index === "number" ? index * ANIMATION_DELAY_DIFFERENCE : 0;

  return (
    <TextAnimationWrapper textStyle={textStyle}>
      <m.div
        initial={{ y: INITIAL_Y_POSITION }}
        animate={shouldAnimate ? { y: 0 } : { y: INITIAL_Y_POSITION }}
        transition={{
          duration: shouldAnimate ? ANIMATION_DURATION : 0,
          ease: "easeOut",
          delay: shouldAnimate ? delay : 0,
        }}
        {...rest}
      >
        {children}
      </m.div>
    </TextAnimationWrapper>
  );
};
